/**
 * camelCase to snake_case (and back) helpers for catalog `payload` blobs.
 *
 * The control-plane admin API serialises responses through `json_response`
 * which converts every key to camelCase. That's the right default for the
 * GUI's typed fields (`updatedAt`, `displayName`, …), but the per-instance
 * `payload` blob is OPAQUE — its consumer is the Rust dataplane, which
 * expects snake_case (`components_catalog`, `image_uri`, `default_env`, …).
 * Without this normalisation, a payload edited in Monaco round-trips
 * through camelCase and the dataplane's `catalog::components_catalog(…)`
 * lookup returns `None`, manifesting as the
 * "Unknown or incomplete customer gateway_type" toast.
 *
 * Scope of the conversion:
 *
 *   - Only object KEYS are rewritten — values (strings, numbers, booleans,
 *     nested objects, arrays) are walked recursively and rewritten in
 *     place when they are themselves objects.
 *   - The conversion is "naïve" by design: `imageUri` → `image_uri`,
 *     `defaultEnv` → `default_env`. We do NOT try to be clever about
 *     consecutive uppercase (`gRPCPort` → `g_r_p_c_port`, not `grpc_port`)
 *     — no key in the catalog schema looks like that, and a smarter
 *     heuristic would introduce ambiguity at the round-trip edge.
 *   - Numeric digits do NOT trigger an underscore (`audioDevice0` →
 *     `audio_device0`, NOT `audio_device_0`).
 *
 * Applied symmetrically by `front/src/api/catalog.ts` on both read
 * (`getInstance`, `getSection`, `listGateways`) and write (`createInstance`,
 * `updateInstance`, `createSection`, `updateSection`) paths, so the Monaco
 * editor always shows the operator the same shape that lands in DDB and
 * that the Rust dataplane reads.
 */

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return (
    typeof value === "object" &&
    value !== null &&
    !Array.isArray(value)
  );
}

/** Convert a single key from camelCase to snake_case. */
export function keyToSnakeCase(key: string): string {
  // Insert "_" before each capital letter, lowercase everything. Stable on
  // already-snake_case input: no capitals → no insertions.
  return key.replace(/[A-Z]/g, (m) => `_${m.toLowerCase()}`);
}

/** Recursively rewrite an object's keys to snake_case. Returns a new value. */
export function toSnakeCase<T = unknown>(value: T): T {
  if (Array.isArray(value)) {
    return value.map((v) => toSnakeCase(v)) as unknown as T;
  }
  if (isPlainObject(value)) {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value)) {
      out[keyToSnakeCase(k)] = toSnakeCase(v);
    }
    return out as unknown as T;
  }
  // Strings, numbers, booleans, null — left alone.
  return value;
}
